<script setup>
/* eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) */
/**
 * CrmProximaAcao — editor da "Próxima Ação" do deal (F1 / §1 + §13 + §6).
 *
 * Mesmo componente montado em DOIS lugares (contrato §6):
 *   • CrmConversationKanban.vue — bloco CRM dentro da conversa (deal ABERTO).
 *   • CrmDealPanel.vue          — gaveta lateral do board.
 *
 * Responsabilidades:
 *   • Lista as ações pendentes (tipo · vencimento · responsável); ATRASADA → sinal
 *     ruby (affordance; sem verde para "ok" — [[feedback-crm-brand-only-data]]).
 *   • Form de criar/editar ação: tipo (taxonomia), vencimento (datetime), responsável
 *     (CrmMultiSelect) e descrição — esta só aparece/obriga quando tipo==='personalizada'.
 *   • Concluir [BLOQUEANTE §13]: se concluir deixaria o deal SEM próxima ação válida
 *     (nenhuma pendente com vencimento ≥ hoje), exige a próxima inline e envia
 *     completeTask({proxima_acao}) ATÔMICO. 422 do guard → mensagem do backend via
 *     useAlert e o form fica aberto.
 *   • Régua de follow-up [F4 / §7]: quando a automação de SLA está ligada
 *     (`sla_automation_enabled` do envelope) e a ação pendente é um TOQUE da régua
 *     (`tipo === 'follow_up'` com `sequencia`), o botão "Concluir" dá lugar a um
 *     SELETOR de 6 resultados. respondeu/interesse/pediu_prazo/outro exigem a próxima
 *     ação (form inline → followUpResult com proxima_acao); nao_respondeu avança a
 *     régua direto; recusou abre o popover de motivo e transforma o deal em perdido.
 *   • Descarte [F4]: ações pendentes comuns (e follow-ups avulsos) ganham um botão
 *     discreto "Descartar" → cancelTask (status cancelada), com confirmação leve.
 *
 * Auto-suficiente: busca as próprias tasks via fetchTasks (não depende do shape do
 * deal do pai). Pessimista de propósito — o guard é autoridade do backend (422); não
 * mostramos um estado "concluído" que a transação pode recusar.
 *
 * White-mode (.crm-light-scope, defesa-em-profundidade). pt-BR locked.
 */
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import crmBoardApi from 'dashboard/api/cw_custom/crmBoard.js';
import CrmMultiSelect from './CrmMultiSelect.vue';
import CrmLoseReasonPopover from './CrmLoseReasonPopover.vue';
import {
  formatDataDiaMesCurto,
  formatHoraCurta,
} from './composables/useMetasFormat.js';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  dealId: { type: [Number, String], required: true },
  // Taxonomia de tipos permitidos p/ (trilha, etapa) — [{ value, label }].
  // Vazio → cai na base canônica (criterion §8.6: base quando sem config).
  actionTypes: { type: Array, default: () => [] },
  // Candidatos a responsável — [{ id, name }]. Vazio → campo responsável oculto
  // (a ação herda o responsável atual do deal via defaultAgenteId).
  agents: { type: Array, default: () => [] },
  defaultAgenteId: { type: [Number, String], default: null },
  // Deal em estado terminal (ganho/perdido) → seção read-only, sem form (§6).
  readonly: { type: Boolean, default: false },
});

// 'changed' — após qualquer mutação bem-sucedida; o pai pode refrescar estado do deal.
const emit = defineEmits(['changed']);

// Mapa canônico de labels pt-BR (§6) — mantido em sincronia com o backend.
const BASE_ACTION_TYPES = [
  { value: 'ligacao', label: 'Fazer ligação' },
  { value: 'proposta', label: 'Enviar proposta' },
  { value: 'follow_up', label: 'Fazer follow-up' },
  { value: 'solicitar_retorno', label: 'Solicitar retorno' },
  { value: 'catalogo', label: 'Enviar catálogo' },
  { value: 'reuniao', label: 'Agendar reunião' },
  { value: 'orcamento', label: 'Enviar orçamento' },
  { value: 'personalizada', label: 'Personalizada' },
];

// Resultados de um toque de follow-up (§7). Labels pt-BR EXATOS do contrato.
//   • requiresNext → exige a próxima ação (form inline; followUpResult+proxima_acao).
//   • loss         → abre o popover de motivo; o deal vira perdido.
//   • sem flag     → nao_respondeu: avança a régua sem próxima ação.
const FOLLOW_UP_RESULTS = [
  { value: 'respondeu', label: 'Cliente respondeu', requiresNext: true },
  { value: 'interesse', label: 'Demonstrou interesse', requiresNext: true },
  { value: 'pediu_prazo', label: 'Pediu prazo', requiresNext: true },
  { value: 'nao_respondeu', label: 'Não respondeu', requiresNext: false },
  { value: 'recusou', label: 'Recusou / sem interesse', loss: true },
  { value: 'outro', label: 'Outro', requiresNext: true },
];

// ── Estado das tasks (auto-fetch) ──
const tasks = ref([]);
const loading = ref(false);
const fetchError = ref('');
// F4 — vêm do envelope de fetchTasks (backend §7). Sem flag → régua desligada.
const slaAutomationEnabled = ref(false);
const followUpIntervals = ref([]);
const followUpTotal = computed(() => followUpIntervals.value.length);

async function loadTasks() {
  loading.value = true;
  fetchError.value = '';
  try {
    const res = await crmBoardApi.fetchTasks(props.accountId, props.dealId);
    const data = res.data ?? {};
    tasks.value = data.tasks ?? (Array.isArray(data) ? data : []);
    slaAutomationEnabled.value = !!data.sla_automation_enabled;
    followUpIntervals.value = Array.isArray(data.follow_up_intervals)
      ? data.follow_up_intervals
      : [];
  } catch {
    fetchError.value = 'Não foi possível carregar as ações.';
  } finally {
    loading.value = false;
  }
}

// Recarrega ao montar e ao trocar de deal (troca de card no painel / de conversa no bloco).
watch(() => props.dealId, loadTasks, { immediate: true });

// ── Classificação ──
const isDone = t =>
  t.status === 'concluida' || t.status === 'cancelada' || t.concluida === true;

// Um TOQUE da régua = follow_up com sequência, e só quando a automação está ligada.
// Follow-ups avulsos (sem sequência) e ações comuns seguem o fluxo Concluir/Descartar.
function isReguaFollowUp(t) {
  return (
    slaAutomationEnabled.value &&
    t.tipo === 'follow_up' &&
    t.sequencia != null &&
    !isDone(t)
  );
}

function venceMs(t) {
  return t.vence_em ? new Date(t.vence_em).getTime() : Infinity;
}

const pendingTasks = computed(() =>
  tasks.value.filter(t => !isDone(t)).sort((a, b) => venceMs(a) - venceMs(b))
);

const doneTasks = computed(() =>
  tasks.value.filter(isDone).sort((a, b) => venceMs(b) - venceMs(a))
);

// Atrasada = derivada (D1). Prefere o `atrasada` do backend; senão calcula.
function isOverdue(t) {
  if (isDone(t) || !t.vence_em) return false;
  if (typeof t.atrasada === 'boolean') return t.atrasada;
  return new Date(t.vence_em).getTime() < Date.now();
}

// Guard §13: uma pendente "cobre" a próxima ação se vence_em ≥ início de hoje.
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
function qualifies(t) {
  return (
    !isDone(t) &&
    !!t.vence_em &&
    new Date(t.vence_em).getTime() >= startOfToday()
  );
}
// Concluir `taskId` deixaria o deal sem próxima ação? (nenhuma OUTRA pendente qualifica)
function needsNextAction(taskId) {
  if (props.readonly) return false;
  return (
    pendingTasks.value.filter(t => t.id !== taskId && qualifies(t)).length === 0
  );
}

// ── Taxonomia / labels ──
const typeOptions = computed(() =>
  props.actionTypes?.length ? props.actionTypes : BASE_ACTION_TYPES
);

function labelFor(value) {
  const all = [...BASE_ACTION_TYPES, ...(props.actionTypes ?? [])];
  return all.find(o => o.value === value)?.label ?? value;
}

// Título da linha: toque da régua vira "Follow-up N de M"; o resto usa o tipo.
function titleFor(task) {
  if (isReguaFollowUp(task)) {
    return followUpTotal.value
      ? `Follow-up ${task.sequencia} de ${followUpTotal.value}`
      : `Follow-up ${task.sequencia}`;
  }
  return labelFor(task.tipo);
}

function assignedName(t) {
  if (t.assigned_to?.name) return t.assigned_to.name;
  const id = t.assigned_to?.id ?? t.assigned_to_id;
  return (props.agents ?? []).find(a => a.id === id)?.name ?? null;
}

const agentOptions = computed(() =>
  (props.agents ?? []).map(a => ({ id: a.id, label: a.name }))
);

// ── Form ──
// mode: 'idle' | 'create' | 'edit' | 'complete' (concluir exigindo a próxima ação)
//       | 'follow_up_next' (registrar resultado do follow-up exigindo a próxima ação).
const mode = ref('idle');
const editingId = ref(null);
const completingId = ref(null);
const submitting = ref(false);
const tried = ref(false);

// F4 — régua de follow-up.
const resultMenuTaskId = ref(null); // seletor de 6 resultados aberto p/ esta task
const lossTaskId = ref(null); // popover de motivo de perda aberto p/ esta task
const followUpTaskId = ref(null); // task cujo resultado exige a próxima ação
const followUpResultValue = ref(''); // qual dos 6 resultados foi escolhido
// Confirmação leve inline (descartar ação comum OU registrar "Não respondeu").
const confirmTaskId = ref(null);
const confirmKind = ref(''); // 'cancel' | 'nao_respondeu'

const fTipo = ref('personalizada');
const fVenceEm = ref('');
const fDescricao = ref('');
const fAgentIds = ref([]);
const tipoOpen = ref(false);

const descricaoRequired = computed(() => fTipo.value === 'personalizada');
const venceEmMissing = computed(() => !fVenceEm.value);
const descricaoMissing = computed(
  () => descricaoRequired.value && !fDescricao.value.trim()
);
const formValid = computed(
  () => !venceEmMissing.value && !descricaoMissing.value
);

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtVence(iso) {
  const data = formatDataDiaMesCurto(iso);
  if (!data) return 'Sem prazo';
  return `${data}, ${formatHoraCurta(iso)}`;
}

function resetFields() {
  fTipo.value = typeOptions.value[0]?.value ?? 'personalizada';
  fVenceEm.value = '';
  fDescricao.value = '';
  fAgentIds.value = props.defaultAgenteId ? [props.defaultAgenteId] : [];
  tried.value = false;
  tipoOpen.value = false;
}

function openCreate() {
  mode.value = 'create';
  editingId.value = null;
  completingId.value = null;
  resetFields();
}

// EDIT é o caminho de CORREÇÃO de uma ação pendente (sem delete livre — F1 não expõe
// hard-delete na UI; stripar a próxima ação violaria o guard §13). Mesmo form de criar.
// F4: cancelar uma ação (status: cancelada) entra aqui como affordance dedicada.
function openEdit(task) {
  mode.value = 'edit';
  editingId.value = task.id;
  completingId.value = null;
  fTipo.value = task.tipo ?? 'personalizada';
  fVenceEm.value = toDatetimeLocal(task.vence_em);
  fDescricao.value = task.descricao ?? '';
  const aid =
    task.assigned_to?.id ?? task.assigned_to_id ?? props.defaultAgenteId;
  fAgentIds.value = aid ? [aid] : [];
  tried.value = false;
  tipoOpen.value = false;
}

function openCompleteNext(taskId) {
  mode.value = 'complete';
  completingId.value = taskId;
  editingId.value = null;
  resetFields();
}

function closeForm() {
  mode.value = 'idle';
  editingId.value = null;
  completingId.value = null;
  followUpTaskId.value = null;
  followUpResultValue.value = '';
  tried.value = false;
  tipoOpen.value = false;
}

function selectTipo(value) {
  fTipo.value = value;
  tipoOpen.value = false;
}

// assigned_to é responsável único — guarda o último selecionado do multiselect.
function lastAgentId() {
  return fAgentIds.value.length
    ? fAgentIds.value[fAgentIds.value.length - 1]
    : null;
}

function buildPayload() {
  const payload = {
    tipo: fTipo.value,
    titulo: labelFor(fTipo.value),
    vence_em: fVenceEm.value || null,
    assigned_to_id: lastAgentId(),
  };
  // descricao: obrigatória em personalizada; senão envia só se o operador escreveu algo.
  if (descricaoRequired.value || fDescricao.value.trim()) {
    payload.descricao = fDescricao.value.trim();
  }
  return payload;
}

async function submitForm() {
  tried.value = true;
  if (!formValid.value || submitting.value) return;
  submitting.value = true;
  try {
    const payload = buildPayload();
    if (mode.value === 'edit' && editingId.value) {
      await crmBoardApi.updateTask(
        props.accountId,
        props.dealId,
        editingId.value,
        payload
      );
    } else {
      await crmBoardApi.createTask(props.accountId, props.dealId, payload);
    }
    await loadTasks();
    closeForm();
    emit('changed');
  } catch (e) {
    useAlert(
      e.response?.data?.error ??
        'Não foi possível salvar a ação. Tente novamente.'
    );
  } finally {
    submitting.value = false;
  }
}

async function runComplete(taskId, proximaAcao) {
  submitting.value = true;
  try {
    await crmBoardApi.completeTask(
      props.accountId,
      props.dealId,
      taskId,
      proximaAcao
    );
    await loadTasks();
    closeForm();
    emit('changed');
  } catch (e) {
    // 422 do guard (ou validação da próxima ação) → mantém o form aberto e alerta.
    useAlert(
      e.response?.data?.error ??
        'Não foi possível concluir a ação. Tente novamente.'
    );
    // Fallback: se o guard barrou um concluir simples, abre o form exigindo a próxima.
    if (!proximaAcao && mode.value !== 'complete') openCompleteNext(taskId);
  } finally {
    submitting.value = false;
  }
}

// Concluir uma ação. Se for a última que cobre o deal → exige a próxima inline.
function completeTask(task) {
  if (props.readonly || submitting.value) return;
  if (needsNextAction(task.id)) {
    openCompleteNext(task.id);
    return;
  }
  runComplete(task.id, null);
}

function submitCompleteNext() {
  tried.value = true;
  if (!formValid.value || submitting.value) return;
  runComplete(completingId.value, buildPayload());
}

// ── F4: régua de follow-up ──

function toggleResultMenu(taskId) {
  if (submitting.value) return;
  resultMenuTaskId.value = resultMenuTaskId.value === taskId ? null : taskId;
}

// Envia o resultado da régua. 422 (falta próxima / motivo inválido) → mantém o
// form/popover aberto e alerta com a mensagem do backend.
async function runFollowUpResult(taskId, payload) {
  if (submitting.value) return;
  submitting.value = true;
  try {
    await crmBoardApi.followUpResult(
      props.accountId,
      props.dealId,
      taskId,
      payload
    );
    await loadTasks();
    closeForm();
    lossTaskId.value = null;
    emit('changed');
  } catch (e) {
    useAlert(
      e.response?.data?.error ??
        'Não foi possível registrar o follow-up. Tente novamente.'
    );
  } finally {
    submitting.value = false;
  }
}

// Escolha de um dos 6 resultados no seletor da régua.
function chooseResult(task, result) {
  resultMenuTaskId.value = null;
  if (result.loss) {
    // recusou → popover de motivo de perda (reuso do CrmLoseReasonPopover).
    lossTaskId.value = task.id;
    return;
  }
  if (result.requiresNext) {
    // respondeu / interesse / pediu_prazo / outro → exige a próxima ação inline.
    mode.value = 'follow_up_next';
    followUpTaskId.value = task.id;
    followUpResultValue.value = result.value;
    completingId.value = null;
    editingId.value = null;
    resetFields();
    return;
  }
  // nao_respondeu → confirmação leve, sem próxima ação (a régua avança sozinha).
  confirmTaskId.value = task.id;
  confirmKind.value = 'nao_respondeu';
}

function submitFollowUpNext() {
  tried.value = true;
  if (!formValid.value || submitting.value) return;
  runFollowUpResult(followUpTaskId.value, {
    resultado: followUpResultValue.value,
    proxima_acao: buildPayload(),
  });
}

// recusou → o popover devolve (motivo, detalhamento|null).
function submitLoss(motivo, detalhe) {
  runFollowUpResult(lossTaskId.value, {
    resultado: 'recusou',
    motivo_perda: motivo,
    motivo_perda_detalhe: detalhe,
  });
}

// ── F4: descarte / confirmação leve ──

function requestCancel(taskId) {
  if (submitting.value) return;
  confirmTaskId.value = taskId;
  confirmKind.value = 'cancel';
}

function dismissConfirm() {
  confirmTaskId.value = null;
  confirmKind.value = '';
}

async function confirmInline() {
  const taskId = confirmTaskId.value;
  const kind = confirmKind.value;
  if (!taskId || submitting.value) return;
  submitting.value = true;
  try {
    if (kind === 'cancel') {
      await crmBoardApi.cancelTask(props.accountId, props.dealId, taskId);
    } else {
      await crmBoardApi.followUpResult(props.accountId, props.dealId, taskId, {
        resultado: 'nao_respondeu',
      });
    }
    await loadTasks();
    dismissConfirm();
    emit('changed');
  } catch (e) {
    useAlert(
      e.response?.data?.error ??
        'Não foi possível concluir a operação. Tente novamente.'
    );
  } finally {
    submitting.value = false;
  }
}

const showForm = computed(() => !props.readonly && mode.value !== 'idle');
const isCompleteMode = computed(() => mode.value === 'complete');
const isFollowUpNextMode = computed(() => mode.value === 'follow_up_next');

// Cabeçalho + rótulo + handler do submit variam conforme o modo do form.
const formHeader = computed(() => {
  if (isCompleteMode.value) return 'Defina a próxima ação para concluir esta.';
  if (isFollowUpNextMode.value)
    return 'Registre a próxima ação para concluir o follow-up.';
  return '';
});
const submitLabel = computed(() => {
  if (isCompleteMode.value)
    return submitting.value ? 'Concluindo…' : 'Concluir e definir próxima';
  if (isFollowUpNextMode.value)
    return submitting.value ? 'Registrando…' : 'Registrar e definir próxima';
  return submitting.value ? 'Salvando…' : 'Salvar ação';
});
function onSubmit() {
  if (isCompleteMode.value) return submitCompleteNext();
  if (isFollowUpNextMode.value) return submitFollowUpNext();
  return submitForm();
}
</script>

<template>
  <div class="crm-light-scope flex flex-col gap-2">
    <!-- ── Lista de ações pendentes ── -->
    <div
      v-for="task in pendingTasks"
      :key="task.id"
      class="bg-n-surface-1 rounded-lg px-3 py-2.5 flex flex-col gap-1.5 border"
      :class="isOverdue(task) ? 'border-n-ruby-6' : 'border-n-weak'"
    >
      <div class="flex items-start gap-2">
        <div class="flex-1 min-w-0 flex flex-col gap-0.5">
          <span
            class="text-mj-13 font-500 text-n-slate-12 truncate inline-flex items-center gap-1.5"
          >
            <span
              v-if="isReguaFollowUp(task)"
              class="i-lucide-git-branch-plus size-3.5 text-n-slate-10 shrink-0"
            />
            {{ titleFor(task) }}
          </span>
          <span
            v-if="task.descricao && task.tipo === 'personalizada'"
            class="text-mj-12 font-400 text-n-slate-10 line-clamp-2"
          >
            {{ task.descricao }}
          </span>
        </div>
        <!-- ATRASADA — único sinal ruby (affordance). Sem verde para "no prazo". -->
        <span
          v-if="isOverdue(task)"
          class="inline-flex items-center gap-1 flex-shrink-0 bg-n-ruby-3 text-n-ruby-11 rounded-md px-1.5 py-0.5 text-mj-11 font-500"
        >
          <span class="i-lucide-alert-triangle size-3" />
          Atrasada
        </span>
      </div>

      <!-- Meta: vencimento + responsável -->
      <div
        class="flex items-center gap-1.5 text-mj-11 text-n-slate-10 flex-wrap"
      >
        <span
          class="inline-flex items-center gap-1 tabular-nums"
          :class="isOverdue(task) && 'text-n-ruby-11 font-500'"
        >
          <span class="i-lucide-calendar-clock size-3" />
          {{ fmtVence(task.vence_em) }}
        </span>
        <template v-if="assignedName(task)">
          <span class="text-n-slate-8">·</span>
          <span class="inline-flex items-center gap-1 truncate">
            <span class="i-lucide-user size-3" />
            {{ assignedName(task) }}
          </span>
        </template>
      </div>

      <!-- Ações da linha — só deal aberto -->
      <div v-if="!readonly" class="pt-0.5">
        <!-- Confirmação leve inline: descartar ação comum OU registrar "Não respondeu". -->
        <div
          v-if="confirmTaskId === task.id"
          class="flex items-center gap-2 flex-wrap"
        >
          <span class="text-mj-12 font-400 text-n-slate-11">
            {{
              confirmKind === 'cancel'
                ? 'Descartar esta ação?'
                : 'Registrar “Não respondeu” neste follow-up?'
            }}
          </span>
          <div class="flex-1" />
          <button
            type="button"
            :disabled="submitting"
            class="h-7 px-2.5 text-mj-12 font-500 text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque disabled:opacity-50"
            @click="dismissConfirm"
          >
            Voltar
          </button>
          <button
            type="button"
            :disabled="submitting"
            class="h-7 px-2.5 inline-flex items-center gap-1 text-mj-12 font-500 rounded-lg transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed"
            :class="
              confirmKind === 'cancel'
                ? 'bg-n-ruby-9 text-white hover:bg-n-ruby-10'
                : 'bg-n-amber-9 text-n-slate-12 hover:bg-n-amber-10'
            "
            @click="confirmInline"
          >
            {{ confirmKind === 'cancel' ? 'Descartar' : 'Confirmar' }}
          </button>
        </div>

        <!-- Régua de follow-up: seletor de 6 resultados (substitui "Concluir"). -->
        <div
          v-else-if="isReguaFollowUp(task)"
          class="relative flex items-center gap-2"
        >
          <div
            v-on-clickaway="() => (resultMenuTaskId = null)"
            class="relative"
          >
            <button
              type="button"
              :disabled="submitting"
              class="h-7 px-2.5 inline-flex items-center gap-1.5 text-mj-12 font-600 rounded-lg bg-n-amber-9 text-n-slate-12 hover:bg-n-amber-10 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
              @click="toggleResultMenu(task.id)"
            >
              <span class="i-lucide-list-checks size-3.5" />
              Registrar resultado
              <span
                class="i-lucide-chevron-down size-3.5 transition-transform duration-mj-toque"
                :class="resultMenuTaskId === task.id && 'rotate-180'"
              />
            </button>
            <div
              v-if="resultMenuTaskId === task.id"
              class="absolute left-0 top-full mt-1 z-50 w-56 bg-n-background border border-n-weak rounded-card shadow-mj-4 flex flex-col py-1"
            >
              <button
                v-for="r in FOLLOW_UP_RESULTS"
                :key="r.value"
                type="button"
                class="px-3 py-2 flex items-center gap-2 text-left text-mj-13 transition-colors duration-mj-toque"
                :class="
                  r.loss
                    ? 'text-n-ruby-11 hover:bg-n-ruby-2'
                    : 'text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="chooseResult(task, r)"
              >
                <span
                  class="size-3.5 shrink-0"
                  :class="
                    r.loss
                      ? 'i-lucide-x-circle text-n-ruby-10'
                      : 'i-lucide-arrow-right text-n-slate-9'
                  "
                />
                <span class="truncate">{{ r.label }}</span>
              </button>
            </div>
          </div>
          <button
            type="button"
            :disabled="submitting"
            class="h-7 px-2.5 inline-flex items-center gap-1 text-mj-12 font-500 text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
            @click="openEdit(task)"
          >
            <span class="i-lucide-pencil size-3" />
            Editar
          </button>

          <!-- recusou → popover de motivo de perda (reuso T5), ancorado à linha. -->
          <CrmLoseReasonPopover
            v-if="lossTaskId === task.id"
            :submitting="submitting"
            @confirm="submitLoss"
            @cancel="lossTaskId = null"
          />
        </div>

        <!-- Ação comum: Concluir / Editar / Descartar. -->
        <div v-else class="flex items-center gap-2">
          <button
            type="button"
            :disabled="submitting"
            class="h-7 px-2.5 inline-flex items-center gap-1 text-mj-12 font-600 rounded-lg bg-n-amber-9 text-n-slate-12 hover:bg-n-amber-10 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
            @click="completeTask(task)"
          >
            <span class="i-lucide-check size-3" />
            Concluir
          </button>
          <button
            type="button"
            :disabled="submitting"
            class="h-7 px-2.5 inline-flex items-center gap-1 text-mj-12 font-500 text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
            @click="openEdit(task)"
          >
            <span class="i-lucide-pencil size-3" />
            Editar
          </button>
          <div class="flex-1" />
          <!-- Descarte (cancelTask): affordance discreta ruby/cinza, sem peso visual. -->
          <button
            type="button"
            :disabled="submitting"
            title="Descartar esta ação"
            class="h-7 px-2 inline-flex items-center gap-1 text-mj-12 font-500 text-n-slate-10 rounded-lg hover:bg-n-ruby-2 hover:text-n-ruby-11 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-ruby-7 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
            @click="requestCancel(task.id)"
          >
            <span class="i-lucide-x size-3" />
            Descartar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Estado vazio (sem ação pendente) ── -->
    <div
      v-if="!pendingTasks.length && !loading && !fetchError"
      class="bg-n-surface-1 border border-n-weak rounded-lg px-3 py-5 flex flex-col items-center gap-1.5 text-center"
    >
      <span class="i-lucide-flag size-5 text-n-slate-8" />
      <span class="text-mj-12 text-n-slate-10">
        Nenhuma próxima ação definida.
      </span>
    </div>

    <!-- ── Erro de carregamento ── -->
    <div
      v-if="fetchError"
      class="bg-n-surface-1 border border-n-weak rounded-lg px-3 py-4 flex flex-col items-center gap-1.5 text-center"
    >
      <span class="i-lucide-alert-circle size-5 text-n-slate-8" />
      <span class="text-mj-12 text-n-slate-10">{{ fetchError }}</span>
      <button
        type="button"
        class="text-mj-12 font-500 text-n-slate-12 underline decoration-n-slate-7 underline-offset-2 hover:decoration-n-amber-9"
        @click="loadTasks"
      >
        Tentar de novo
      </button>
    </div>

    <!-- ── Form (criar / editar / concluir→próxima / resultado→próxima) ── -->
    <div
      v-if="showForm"
      class="border border-n-slate-12 rounded-lg px-3 py-3 flex flex-col gap-2.5 bg-n-surface-1"
    >
      <!-- Cabeçalho: explicita a governança (§13) ao concluir/registrar resultado. -->
      <div
        v-if="formHeader"
        class="flex items-start gap-1.5 text-mj-12 font-500 text-n-slate-11"
      >
        <span
          class="i-lucide-arrow-right-circle size-3.5 mt-px flex-shrink-0 text-n-slate-10"
        />
        <span>{{ formHeader }}</span>
      </div>

      <!-- Tipo — single-select -->
      <div class="flex flex-col gap-1">
        <span class="text-mj-12 font-500 text-n-slate-10"> Tipo </span>
        <div v-on-clickaway="() => (tipoOpen = false)" class="relative">
          <button
            type="button"
            class="w-full h-9 px-2.5 flex items-center justify-between gap-2 rounded-lg border bg-n-background transition-colors duration-mj-toque"
            :class="
              tipoOpen
                ? 'border-n-slate-8 ring-2 ring-n-slate-7/40'
                : 'border-n-weak hover:border-n-slate-7'
            "
            @click="tipoOpen = !tipoOpen"
          >
            <span class="text-mj-13 text-n-slate-12 truncate">
              {{ labelFor(fTipo) }}
            </span>
            <span
              class="i-lucide-chevron-down size-4 text-n-slate-9 shrink-0 transition-transform duration-mj-toque"
              :class="tipoOpen && 'rotate-180'"
            />
          </button>
          <div
            v-if="tipoOpen"
            class="absolute left-0 right-0 top-full mt-1 z-50 max-h-56 overflow-y-auto crm-scrollbar-thin bg-n-background border border-n-weak rounded-card shadow-mj-4 flex flex-col py-1"
          >
            <button
              v-for="opt in typeOptions"
              :key="opt.value"
              type="button"
              class="px-3 py-2 flex items-center gap-2 text-left text-mj-13 hover:bg-n-alpha-1 transition-colors duration-mj-toque"
              :class="
                opt.value === fTipo
                  ? 'text-n-slate-12 font-500'
                  : 'text-n-slate-11'
              "
              @click="selectTipo(opt.value)"
            >
              <span
                class="i-lucide-check size-3.5 shrink-0 text-n-slate-12"
                :class="opt.value === fTipo ? 'opacity-100' : 'opacity-0'"
              />
              <span class="truncate">{{ opt.label }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Vencimento -->
      <div class="flex flex-col gap-1">
        <span class="text-mj-12 font-500 text-n-slate-10"> Vencimento </span>
        <input
          v-model="fVenceEm"
          type="datetime-local"
          aria-label="Vencimento da ação"
          :aria-invalid="tried && venceEmMissing"
          class="h-9 px-2.5 text-mj-13 text-n-slate-12 font-400 rounded-lg border bg-n-background tabular-nums focus:outline-none focus:ring-1 focus:border-transparent transition-colors duration-mj-toque"
          :class="
            tried && venceEmMissing
              ? 'border-n-ruby-7 focus:ring-n-ruby-8'
              : 'border-n-weak focus:ring-n-slate-8'
          "
        />
        <p
          v-if="tried && venceEmMissing"
          class="px-0.5 text-mj-11 font-400 text-n-ruby-11"
        >
          Defina data e hora de vencimento.
        </p>
      </div>

      <!-- Responsável — só quando há agentes disponíveis -->
      <div v-if="agentOptions.length" class="flex flex-col gap-1">
        <span class="text-mj-12 font-500 text-n-slate-10"> Responsável </span>
        <CrmMultiSelect
          v-model="fAgentIds"
          :options="agentOptions"
          placeholder="Sem responsável"
          empty-text="Nenhum agente disponível."
        />
      </div>

      <!-- Descrição — só (e obrigatória) quando tipo === personalizada -->
      <div v-if="descricaoRequired" class="flex flex-col gap-1">
        <span class="text-mj-12 font-500 text-n-slate-10"> Descrição </span>
        <textarea
          v-model="fDescricao"
          rows="2"
          maxlength="500"
          placeholder="O que precisa ser feito?"
          aria-label="Descrição da ação"
          :aria-invalid="tried && descricaoMissing"
          class="px-2.5 py-2 text-mj-13 text-n-slate-12 font-400 rounded-lg border bg-n-background placeholder:text-n-slate-10 resize-none focus:outline-none focus:ring-1 focus:border-transparent transition-colors duration-mj-toque"
          :class="
            tried && descricaoMissing
              ? 'border-n-ruby-7 focus:ring-n-ruby-8'
              : 'border-n-weak focus:ring-n-slate-8'
          "
        />
        <p
          v-if="tried && descricaoMissing"
          class="px-0.5 text-mj-11 font-400 text-n-ruby-11"
        >
          Descreva a ação personalizada.
        </p>
      </div>

      <!-- Ações do form -->
      <div class="flex items-center gap-2 justify-end pt-0.5">
        <button
          type="button"
          :disabled="submitting"
          class="h-8 px-3 text-mj-12 font-500 text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors duration-mj-toque disabled:opacity-50"
          @click="closeForm"
        >
          Cancelar
        </button>
        <button
          type="button"
          :disabled="submitting"
          class="h-8 px-3 inline-flex items-center gap-1.5 text-mj-12 font-600 rounded-lg bg-n-amber-9 text-n-slate-12 hover:bg-n-amber-10 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed"
          @click="onSubmit"
        >
          <span
            v-if="isCompleteMode || isFollowUpNextMode"
            class="i-lucide-check size-3.5"
          />
          {{ submitLabel }}
        </button>
      </div>
    </div>

    <!-- ── Botão "Nova ação" — só deal aberto e sem form aberto ── -->
    <button
      v-if="!readonly && mode === 'idle'"
      type="button"
      class="w-full h-8 inline-flex items-center justify-center gap-1.5 text-mj-12 font-500 text-n-slate-11 border border-dashed border-n-weak rounded-lg hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
      @click="openCreate"
    >
      <span class="i-lucide-plus size-3.5" />
      Nova ação
    </button>

    <!-- ── Histórico (ações concluídas) — read-only, atenuado ── -->
    <div v-if="doneTasks.length" class="flex flex-col gap-1 pt-1">
      <span class="text-mj-12 font-500 text-n-slate-10"> Concluídas </span>
      <div
        v-for="task in doneTasks"
        :key="task.id"
        class="flex items-center gap-2 px-2.5 py-1.5 text-mj-12 text-n-slate-10"
      >
        <span
          class="i-lucide-check-square size-3.5 flex-shrink-0 text-n-slate-9"
        />
        <span class="line-through truncate flex-1">{{
          labelFor(task.tipo)
        }}</span>
        <span
          v-if="task.vence_em"
          class="tabular-nums flex-shrink-0 text-n-slate-9"
        >
          {{ fmtVence(task.vence_em) }}
        </span>
      </div>
    </div>
  </div>
</template>
